/**
 * React Query wrapper around the per-user sidebar preferences
 * (`/api/v1/profile/me/sidebar-preferences/`).
 *
 * One cache entry per user, shared by the desktop shell, the mobile menu, the
 * Settings "Sidebar" tab and onboarding — so hiding a group anywhere updates
 * every surface, on every layout, without a reload. Stored `hiddenGroups` for
 * a group whose feature is currently disabled are kept verbatim: re-enabling
 * the feature restores the user's choice.
 */

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getSidebarPreferences,
  normalizeSidebarHiddenGroups,
  updateSidebarPreferences,
  type SidebarPreferences,
} from "./sidebar-preferences";

export const sidebarPreferencesKeys = {
  all: ["sidebar-preferences"] as const,
  user: (userId: string | null | undefined) =>
    ["sidebar-preferences", userId ?? "anonymous"] as const,
};

const EMPTY: SidebarPreferences = { hiddenGroups: [] };

export function useSidebarPreferences(
  userId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const key = sidebarPreferencesKeys.user(userId);

  const query = useQuery<SidebarPreferences>({
    queryKey: key,
    queryFn: getSidebarPreferences,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const mutation = useMutation<
    SidebarPreferences,
    Error,
    readonly string[],
    { previous: SidebarPreferences | undefined }
  >({
    mutationFn: (hiddenGroups) => updateSidebarPreferences(hiddenGroups),
    onMutate: async (hiddenGroups) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SidebarPreferences>(key);
      queryClient.setQueryData<SidebarPreferences>(key, {
        hiddenGroups: normalizeSidebarHiddenGroups(hiddenGroups),
      });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      queryClient.setQueryData(key, context?.previous ?? EMPTY);
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(key, preferences);
    },
  });

  const { mutateAsync } = mutation;
  const updateHiddenGroups = useCallback(
    async (hiddenGroups: readonly string[]) => {
      await mutateAsync(hiddenGroups);
    },
    [mutateAsync],
  );
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: sidebarPreferencesKeys.user(userId),
    });
  }, [queryClient, userId]);

  return {
    hiddenGroups: enabled ? (query.data ?? EMPTY).hiddenGroups : EMPTY.hiddenGroups,
    isLoading: enabled && query.isLoading,
    updateHiddenGroups,
    refresh,
  };
}
